/**
 * Data layer for `volume-based-pricing-per-click-per-conversion`. Thin typed fetch wrappers over /api/v1/volume-based-pricing-per-click-per-conversion.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface VolumeBasedPricingPerClickPerConversionRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListVolumeBasedPricingPerClickPerConversionResponse {
	count: number;
	items: VolumeBasedPricingPerClickPerConversionRow[];
}

export interface CreateVolumeBasedPricingPerClickPerConversionBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/volume-based-pricing-per-click-per-conversion";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListVolumeBasedPricingPerClickPerConversionResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListVolumeBasedPricingPerClickPerConversionResponse>(res);
}

export async function create(body: CreateVolumeBasedPricingPerClickPerConversionBody): Promise<VolumeBasedPricingPerClickPerConversionRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<VolumeBasedPricingPerClickPerConversionRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const volumeBasedPricingPerClickPerConversionQueryKeys = {
	all: ["volume-based-pricing-per-click-per-conversion"] as const,
	list: () => [...volumeBasedPricingPerClickPerConversionQueryKeys.all] as const,
	detail: (id: string) => [...volumeBasedPricingPerClickPerConversionQueryKeys.all, id] as const,
};
